/* eslint-disable prettier/prettier */
import { CollapsibleSection } from '../CollapsibleSection';
import { KeyValuePair } from '../../ui/KeyValuePair';
import { getFieldValue } from '../../../utils/getFieldValue';
import { LayerRenderer } from './LayerRenderer';
import { emojiIconMap } from '../../../utils/emojiIconMap';
import { TreatyEntityType } from '../../../types/TreatyEntityType';
import { useCurrencyOptions } from '../../../hooks/useCurrencyOptions';

export const DynamicSectionRenderer = ({
  sections,
  refreshTreatyData,
}: {
  sections: any[];
  refreshTreatyData?: () => void;
}) => {
  const { currencies } = useCurrencyOptions();

  const fieldConfigMap: Record<string, any> = {
    typeOfXL: {
      type: 'select',
      options: ['Per Event', 'Per Risk', 'Per Event or Risk', 'Aggregate', 'Stop Loss'],
    },
    premiumRate: {
      type: 'percent',
    },
    layerCurrency: {
      type: 'select',
      options: currencies.map((c) => c.value),
    },
  };

  return (
    <>
      {sections.map((section, index) => {
        if (section.type === 'PROPORTIONAL') {
          const prop = section.proportional;
          const type = prop.type.replace(/_/g, ' ');
          const title = `${type} - Proportional`;

          return (
            <CollapsibleSection key={index} title={title} icon={<span>{emojiIconMap[title]}</span>}>
              {Object.entries(prop)
                .filter(([k]) => k !== 'id' && k !== 'type')
                .map(([key, value]) => {
                  const { value: fieldValue, ai_result } = getFieldValue(value);
                  return (
                    <KeyValuePair
                      key={key}
                      label={formatKey(key)}
                      value={fieldValue}
                      aiValue={ai_result}
                      entityId={prop.id}
                      entityType={TreatyEntityType.PROPORTIONAL_SECTION}
                      fieldKey={key}
                      editable
                      refreshTreatyData={refreshTreatyData}
                      fieldConfig={fieldConfigMap[key]}
                    />
                  );
                })}
            </CollapsibleSection>
          );
        }

        if (section.type === 'NON_PROPORTIONAL') {
          const np = section.non_proportional;
          const type = np.type.replace(/_/g, ' ');
          const title = `${type} - Non Proportional`;

          const { value: gnpiValue, ai_result: gnpiAi } = getFieldValue(np.GNPI);

          return (
            <CollapsibleSection key={index} title={title} icon={<span>{emojiIconMap[title]}</span>}>
              <KeyValuePair
                label="GNPI"
                value={gnpiValue}
                aiValue={gnpiAi}
                entityId={np.id}
                entityType={TreatyEntityType.NON_PROPORTIONAL_SECTION}
                fieldKey="GNPI"
                editable
                refreshTreatyData={refreshTreatyData}
              />

              {np.type === 'EXCESS_OF_LOSS' && np.typeOfXL && (
                <KeyValuePair
                  label="Type of XL"
                  value={getFieldValue(np.typeOfXL).value}
                  aiValue={getFieldValue(np.typeOfXL).ai_result}
                  entityId={np.id}
                  entityType={TreatyEntityType.NON_PROPORTIONAL_SECTION}
                  fieldKey="typeOfXL"
                  editable
                  refreshTreatyData={refreshTreatyData}
                  fieldConfig={fieldConfigMap['typeOfXL']}
                />
              )}

              <div className="mb-3" />
              {np.layers.map((layer: any, i: number) => (
                <LayerRenderer
                  key={i}
                  layer={layer}
                  index={i + 1}
                  fieldConfigMap={fieldConfigMap}
                  refreshTreatyData={refreshTreatyData}
                />
              ))}
            </CollapsibleSection>
          );
        }

        return null;
      })}
    </>
  );
};

const formatKey = (key: string) =>
  key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
